/**
 * KYC Settings API — `/admin/kyc/settings` on the px backend.
 *
 * Which of the six KYC steps are switched on: a global default and per-country
 * overrides. Step 1 starts KYC and is always on. A step switched off is skipped
 * by the app: the next step opens once the previous switched-on step is done.
 */

const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  return base.endsWith("/admin") ? `${base}/kyc` : `${base}/admin/kyc`;
};

const headers = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === false) {
    const err = new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
    err.response = data;
    throw err;
  }
  return data;
};

const request = async (method, path, body) => {
  const res = await fetch(`${getApiBase()}${path}`, {
    method,
    headers: headers(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return handleResponse(res);
};

/** `{ step4Enabled: true }` for step 4 on. */
export const stepFlag = (n, on) => ({ [`step${n}Enabled`]: !!on });

export const fetchKycSettings = () => request("GET", "/settings");
/**
 * One step: off, on in all countries, or on only in the selected countries.
 * `{ enabled: true, scope: "selected", countries: ["IN", "BD"] }`
 */
export const saveStepScope = (n, { enabled, scope, countries }) =>
  request("PUT", `/settings/steps/${encodeURIComponent(n)}`, { enabled, scope, countries });
export const saveGlobalSteps = (flags) => request("PUT", "/settings/global", flags);
export const saveCountrySteps = (country, flags) => request("PUT", `/settings/countries/${encodeURIComponent(country)}`, flags);
export const deleteCountryOverride = (country) => request("DELETE", `/settings/countries/${encodeURIComponent(country)}`);

/**
 * Put a user's KYC step back to not done — and every step after it (a later
 * step cannot stand on an undone one). Step 4 or earlier also releases their
 * verified phone number. A note (5–300 characters) is required. Needs `users:edit`.
 */
export const revertUserKycStep = (userId, step, kycId, note) =>
  request("POST", `/users/${encodeURIComponent(userId)}/revert`, { step, note, ...(kycId ? { kycId } : {}) });

/** The user's six steps as the app sees them, plus `reverts` (who, when, step, note). */
export const fetchUserKyc = (userId) => request("GET", `/users/${encodeURIComponent(userId)}`);

export const REVERT_NOTE_MIN = 5;
export const REVERT_NOTE_MAX = 300;
